import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { productsApi, categoriesApi, checkoutApi, cartApi } from "../lib/api";

const AppContext = createContext(null);
const CART_TOKEN_KEY = "daporkoe_cart_token";

const emptyShipping = {
  name: "",
  phone: "",
  address: "",
  province_id: "",
  province: "",
  city_id: "",
  district_id: "",
  subdistrict_id: "",
  kelurahan: "",
  kecamatan: "",
  kota: "",
  zip_code: "",
  notes: "",
};

const normalizeProduct = (product) => {
  const categoryName =
    typeof product.category === "string"
      ? product.category
      : product.category?.name || product.category_name || "Produk Herbal";

  return {
    ...product,
    category: categoryName,
    category_id: product.category_id || product.category?.id,
    benefit: product.benefit || product.benefits || product.short_description || product.description || "",
    benefits: product.benefits || product.benefit || "",
    short_description: product.short_description || product.description || "",
    active: product.active ?? product.is_active ?? true,
    is_active: product.is_active ?? product.active ?? true,
    gallery: Array.isArray(product.gallery) ? product.gallery : [],
  };
};

const createCartToken = () => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const getOrCreateCartToken = () => {
  if (typeof window === "undefined") return createCartToken();
  const existing = window.localStorage.getItem(CART_TOKEN_KEY);
  if (existing && existing.length >= 24) return existing;
  const token = createCartToken();
  window.localStorage.setItem(CART_TOKEN_KEY, token);
  return token;
};

const normalizeCartResponse = (cartResponse) =>
  (cartResponse?.items || []).map((item) => ({
    productId: item.product_id,
    quantity: item.quantity,
  }));

export const AppProvider = ({ children }) => {
  const [cartToken] = useState(getOrCreateCartToken);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // checkout flow state
  const [shipping, setShipping] = useState(emptyShipping);
  const [selectedCourierId, setSelectedCourierId] = useState("");
  const [selectedPaymentId, setSelectedPaymentId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Load data from API on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load products from API
        const productsData = await productsApi.getAll();
        setProducts((productsData || []).map(normalizeProduct));
        
        const [categoriesData, paymentMethodsData, cartData] = await Promise.all([
          categoriesApi.getAll(),
          checkoutApi.getPaymentMethods(),
          cartApi.get(cartToken),
        ]);
        setCategories(categoriesData || []);
        setPaymentMethods(paymentMethodsData || []);
        setCart(normalizeCartResponse(cartData));
        
      } catch (error) {
        console.error('Error loading data:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [cartToken]);

  const upsertProduct = useCallback((product) => {
    setProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) return prev.map((p) => (p.id === product.id ? product : p));
      return [...prev, product];
    });
  }, []);

  const deleteProduct = useCallback((id) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setCart((prev) => prev.filter((i) => i.productId !== id));
  }, []);

  const setCheckoutCouriers = useCallback((rates) => {
    setCouriers(rates || []);
    setSelectedCourierId("");
    setAppliedCoupon(null);
  }, []);

  const applyServerCart = useCallback((response) => {
    setCart(normalizeCartResponse(response));
  }, []);

  // --- CART ---
  const addToCart = useCallback((productId, qty = 1) => {
    const current = cart.find((item) => item.productId === productId);
    const nextQuantity = (current?.quantity || 0) + qty;
    setCart((prev) => {
      const existing = prev.find((i) => i.productId === productId);
      if (existing) {
        return prev.map((i) =>
          i.productId === productId ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [...prev, { productId, quantity: qty }];
    });
    cartApi
      .upsertItem(cartToken, { product_id: productId, quantity: nextQuantity })
      .then(applyServerCart)
      .catch((error) => console.error("Gagal menyimpan cart:", error));
  }, [applyServerCart, cart, cartToken]);

  const setCartQty = useCallback((productId, qty) => {
    setCart((prev) => {
      if (qty <= 0) return prev.filter((i) => i.productId !== productId);
      return prev.map((i) =>
        i.productId === productId ? { ...i, quantity: qty } : i
      );
    });
    const request = qty <= 0
      ? cartApi.removeItem(cartToken, productId)
      : cartApi.upsertItem(cartToken, { product_id: productId, quantity: qty });
    request
      .then(applyServerCart)
      .catch((error) => console.error("Gagal mengubah cart:", error));
  }, [applyServerCart, cartToken]);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((i) => i.productId !== productId));
    cartApi
      .removeItem(cartToken, productId)
      .then(applyServerCart)
      .catch((error) => console.error("Gagal menghapus item cart:", error));
  }, [applyServerCart, cartToken]);

  const clearCart = useCallback(() => {
    setCart([]);
    cartApi
      .clear(cartToken)
      .then(applyServerCart)
      .catch((error) => console.error("Gagal mengosongkan cart:", error));
  }, [applyServerCart, cartToken]);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);

  const resetCheckout = useCallback(() => {
    setShipping(emptyShipping);
    setSelectedCourierId("");
    setSelectedPaymentId("");
    setCouponCode("");
    setAppliedCoupon(null);
  }, []);

  // cart items joined with product details
  const cartItems = cart
    .map((i) => {
      const p = products.find((x) => x.id === i.productId);
      if (!p) return null;
      const quantity = Math.min(i.quantity, Math.max(1, p.stock));
      return {
        productId: p.id,
        productName: p.name,
        image: p.image,
        price: p.price,
        comparePrice: p.compare_price,
        stock: p.stock,
        quantity,
        subtotal: p.price * quantity,
      };
    })
    .filter(Boolean);

  const cartSubtotal = cartItems.reduce((s, i) => s + i.subtotal, 0);
  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);

  const selectedCourier = couriers.find((c) => c.id === selectedCourierId) || null;
  const selectedPayment = paymentMethods.find((p) => p.code === selectedPaymentId) || null;

  useEffect(() => {
    setAppliedCoupon(null);
  }, [cartSubtotal, selectedCourier?.cost]);

  const cartTotal = Math.max(
    cartSubtotal + (selectedCourier?.cost || 0) - (appliedCoupon?.discount_amount || 0),
    0,
  );

  const createOrder = useCallback(
    async ({ payment }) => {
      if (cartItems.length === 0) return null;
      if (!selectedCourier || !payment) return null;

      const response = await checkoutApi.createOrder({
        customer: {
          full_name: shipping.name,
          phone: shipping.phone,
          address: shipping.address,
          province_id: shipping.province_id ? Number(shipping.province_id) : null,
          province: shipping.province || null,
          city_id: shipping.city_id ? Number(shipping.city_id) : null,
          district_id: shipping.district_id ? Number(shipping.district_id) : null,
          subdistrict_id: shipping.subdistrict_id ? Number(shipping.subdistrict_id) : null,
          kelurahan: shipping.kelurahan || null,
          kecamatan: shipping.kecamatan || null,
          kota: shipping.kota || null,
          zip_code: shipping.zip_code || null,
          notes: shipping.notes || null,
        },
        cart_token: cartToken,
        items: cartItems.map((i) => ({
          product_id: i.productId,
          quantity: i.quantity,
        })),
        payment_method: payment.code,
        shipping_cost: selectedCourier.cost,
        courier_code: selectedCourier.code,
        courier_name: selectedCourier.name,
        courier_service: selectedCourier.service,
        courier_etd: selectedCourier.etd,
        discount_code: appliedCoupon?.code || null,
        notes: shipping.notes || null,
      });
      const invoice = response?.sale?.invoice;
      setCart([]);
      resetCheckout();
      return invoice;
    },
    [appliedCoupon, cartItems, cartToken, selectedCourier, shipping, resetCheckout]
  );

  return (
    <AppContext.Provider
      value={{
        products,
        categories,
        couriers,
        setCheckoutCouriers,
        paymentMethods,
        cartToken,
        cart,
        cartItems,
        cartSubtotal,
        cartCount,
        cartTotal,
        couponCode,
        setCouponCode,
        appliedCoupon,
        setAppliedCoupon,
        cartOpen,
        loading,
        openCart,
        closeCart,
        shipping,
        setShipping,
        selectedCourierId,
        setSelectedCourierId,
        selectedCourier,
        selectedPaymentId,
        setSelectedPaymentId,
        selectedPayment,
        upsertProduct,
        deleteProduct,
        addToCart,
        setCartQty,
        removeFromCart,
        clearCart,
        createOrder,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
